import { useRouter } from '@abyss/web/hooks/useRouter';
import { find } from 'lodash';
import { useEffect, useState } from 'react';

import { ConstantsLagoon } from '../../../common/ConstantsLagoon';
import { usePortalSignInData } from '../../../common/GuestHeader/utils';
import { useLagoon } from '../../../hooks/useLagoon';
import { getLocale } from '../../../utils/providerSearch.utils';
import { callGuestSessionUrl } from '../../../utils/session.utils';
import { redirectToGuestSignIn } from '../Guest.utils';
import { clearGuestSession } from '../utils';
import { GuestTimeoutModal } from './GuestTimeoutModal';
import {
  GuestTimeoutModalWarning,
  WARNING_MINUTES,
} from './GuestTimeoutModalWarning';

const ACTIONS = ['mousedown', 'focus', 'keydown'];

export const WARNING_MILLIS = WARNING_MINUTES * 60 * 1000;
export const GuestTimeoutModalWrapper = () => {
  const { shouldShowSignIn } = usePortalSignInData();
  const sessionExpiredHash = window.location.hash.includes(
    'sessionExpired=true'
  );

  const [isInactive, setIsInactive] = useState(sessionExpiredHash);
  const [isSessionExpired, setIsSessionExpired] = useState(sessionExpiredHash);

  const { getSearchParams } = useRouter();

  const config = useLagoon('config')();
  const [searchParams] = getSearchParams();
  const lob = searchParams?.get('planSelectionLob') || 'EI';

  const signInUrl = config?.find(
    (row) => row.key === `SIGNIN_URL_${lob.toUpperCase()}`
  )?.value;

  const sessionTimeoutMillis = find(config, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.SESSION_TIMEOUT_MILLIS,
  })?.value;

  const sessionTimeoutWarningMillis =
    sessionTimeoutMillis > WARNING_MILLIS
      ? sessionTimeoutMillis - WARNING_MILLIS
      : 0;

  const addEvents = (listener) => {
    ACTIONS.forEach((eventName) => {
      window.addEventListener(eventName, listener);
    });
  };

  const removeEvents = (listener) => {
    ACTIONS.forEach((eventName) => {
      window.removeEventListener(eventName, listener);
    });
  };

  useEffect(() => {
    if (!shouldShowSignIn) {
      return () => {};
    }
    const idleTimeout = () =>
      setTimeout(() => {
        if (sessionTimeoutWarningMillis > 0) {
          setIsInactive(true);
        }
      }, sessionTimeoutWarningMillis);

    const logoutTimeout = () =>
      setTimeout(() => {
        setIsSessionExpired(true);
        clearGuestSession();
        window.location.href = '/guest-plan-selection#sessionExpired=true';
      }, WARNING_MILLIS);

    let timeout = isInactive ? logoutTimeout() : idleTimeout();

    const listener = () => {
      if (!isInactive) {
        clearTimeout(timeout);
        timeout = idleTimeout();
      }
    };

    addEvents(listener);

    return () => {
      removeEvents(listener);
      clearTimeout(timeout);
    };
  }, [isInactive, sessionTimeoutWarningMillis, shouldShowSignIn]);

  const handleCloseInactive = () => {
    setIsInactive(false);
  };

  const handleCloseSessionExpired = () => {
    window.location.hash = '';
    callGuestSessionUrl(true);
    setIsSessionExpired(false);
    setIsInactive(false);
  };

  const handleLogin = () => {
    redirectToGuestSignIn(signInUrl, getLocale());
  };

  if (!shouldShowSignIn) {
    return null;
  }

  if (isSessionExpired) {
    return (
      <GuestTimeoutModal
        handleClose={handleCloseSessionExpired}
        handleLogin={handleLogin}
      />
    );
  }

  if (isInactive) {
    return (
      <GuestTimeoutModalWarning
        handleClose={handleCloseInactive}
        handleLogin={handleLogin}
      />
    );
  }

  return null;
};
